import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect,
} from 'react';
import {useDispatch} from 'react-redux';
import {BaseComponent} from 'web/components/BaseComponent';
import enumList from 'web/utils/enumList';
import CustomToolbar from './CustomToolbar';
import {Calendar, momentLocalizer} from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/sass/styles.scss';
import {fetchDispatchList} from 'web/redux/actions/DispatchActions';
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
} from 'react-calendar-timeline';
import 'web/css/react-calendar-timeline.css';
import useCalendar from '../hooks/useCalendar';
import useDebounce from '../hooks/useDebounce';
import EmployeeSearch from './EmployeeSearch';
import DataTableLoading from 'web/components/DataTableLoading';

const DispatchCalendarPage = (props) => {
  const {
    onNewDispatch,
    onUpdateDispatch,
    setEmployeeCode,
    setStartTime,
    setEndTime,
    dispatchSelection,
    employeeSelection,
    reloadDispatchCalendar,
    reloadEmployee,
    isLoading,
  } = props;
  const dispatch = useDispatch();

  const handleSelectSlot = ({code, start, end}) => {
    setEmployeeCode(code);
    start = moment(start).set({hour: 8, minute: 30, second: 0, millisecond: 0});
    end = moment(start).set({hour: 17, minute: 30, second: 0, millisecond: 0});
    setStartTime(start);
    setEndTime(end);
    onNewDispatch();
  };

  const {
    items,
    groups,
    events,
    dragItem,
    currentView,
    summaryEvents,
    setSummaryEvents,
    setGroups,
    setEvents,
    setItems,
    setCurrentView,
    generateFormattedEvent,
    generateSummaryEvents,
    handleItemMove,
    handleItemResize,
    handleCanvasClick,
    handleItemDrag,
    handleItemDoubleClick,
  } = useCalendar(handleSelectSlot, onUpdateDispatch);

  const baseComponent = new BaseComponent(props);

  const [culture, setCulture] = useState('zh-TW');
  const calendarWrapperRef = useRef(null);
  const [date, setDate] = useState(moment().startOf('day'));
  const localizer = momentLocalizer(moment);

  const lang = {
    month: '月',
    week: '週',
    day: '日',
    agenda: '清單',
    work_week: '工作日',
    today: '今天',
    previous: '往上',
    next: '往下',
  };

  useEffect(() => {
    baseComponent.checkPermission(
      enumList.moduleEnums.case,
      enumList.accessModeEnums.read,
    );
  }, []);

  useEffect(() => {
    if (employeeSelection) {
      const formattedGroups = employeeSelection.map(
        ({employeeName, employeeCode}) => ({
          id: employeeCode,
          title: `${employeeName} - ${employeeCode}`,
          code: employeeCode,
        }),
      );
      setGroups(formattedGroups);
    }
  }, [employeeSelection]);

  useLayoutEffect(() => {
    if (!dispatchSelection || dispatchSelection.length === 0) return;

    const formattedEvents = generateFormattedEvent(dispatchSelection);
    const summaryConfig = currentView === 'month' ? 'date' : 'hour';
    const summaryEvents = generateSummaryEvents(formattedEvents, summaryConfig);

    setSummaryEvents(summaryEvents);
    setItems(formattedEvents);
    setEvents(summaryEvents);
  }, [dispatchSelection, currentView]);
  const debouncedDispatchForDateLabel = useDebounce(
    reloadDispatchCalendar,
    500,
  );
  const debouncedDispatchForEmployeeNameSearch = useDebounce(
    (date, employeeName) => {
      reloadDispatchCalendar(date, employeeName);
      reloadEmployee(employeeName);
    },
    500,
  );
  const {components, views, messages} = useMemo(
    () => ({
      components: {
        event: ({event}) => {
          return (
            <div
              className="d-flex justify-content-center
              w-100 h-100 align-items-center"
              onClick={() => {
                setDate(event.start);
                setCurrentView('day');
              }}
            >
              <b>{event.title}</b>
            </div>
          );
        },
        toolbar: CustomToolbar,
      },
      messages: lang,
      views: {
        month: true,
        week: true,
        day: true,
      },
    }),
    [],
  );

  const handleDateChange = (newDate) => {
    const newDateStr = moment(newDate).format('YYYY-MM');
    const oldDateStr = moment(date).format('YYYY-MM');
    if (newDateStr !== oldDateStr) {
      debouncedDispatchForDateLabel(newDateStr);
    }
    setDate(newDate);
  };

  const CustomTimelineHeader = () => {
    const handleNavigate = (action) => {
      const delta = action === 'NEXT' ? 1 : action === 'PREV' ? -1 : 0;
      if (action === 'TODAY') {
        const todayStart = moment().startOf('day');
        handleDateChange(todayStart);
      } else {
        handleDateChange(moment(date).add(delta, 'day'));
      }
    };

    const timelineLabel = `${moment(date).format('YYYY/MM/DD')}`;

    const timelineMessages = {
      month: '月',
      week: '週',
      day: '日',
      agenda: '清單',
      work_week: '工作日',
      today: '今天',
      previous: '往上',
      next: '往下',
    };

    return (
      <CustomToolbar
        label={timelineLabel}
        messages={timelineMessages}
        view={currentView}
        views={['month', 'week', 'day']}
        onView={setCurrentView}
        onNavigate={handleNavigate}
      />
    );
  };

  return (
    <>
      <section id="datatable-container">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <svg className="nav__icon nav__icon--breadcrumb me-3">
                <use href="#ico-home-line"></use>
              </svg>
              派工管理
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              派工管理月曆
            </li>
          </ol>
        </nav>
        <h4>派工管理月曆</h4>
        <DataTableLoading {...{isLoading}} />
        {currentView === 'day' ? (
          <>
            <EmployeeSearch
              reloadDispatchCalendar={(employeeName) =>
                debouncedDispatchForEmployeeNameSearch(date, employeeName)
              }
            />
            <CustomTimelineHeader />
            <Timeline
              groups={groups}
              items={items}
              visibleTimeStart={date.valueOf()}
              visibleTimeEnd={date.valueOf() + 24 * 60 * 60 * 1000}
              dragSnap={60 * 1000} // 拖曳間隔
              maxZoom={24 * 60 * 60 * 1000} // 縮放最大值
              minZoom={24 * 60 * 60 * 1000} // 縮放最小值
              onItemResize={handleItemResize}
              onItemMove={handleItemMove}
              onItemDrag={handleItemDrag}
              onItemDoubleClick={handleItemDoubleClick}
              onCanvasClick={handleCanvasClick}
              canMove
              canResize="both"
              stackItems
              buffer={1}
              lineHeight={50}
              itemHeightRatio={0.75}
              itemRenderer={(props) => <Item {...props} />}
              groupRenderer={({group}) => (
                <div>
                  <span className="d-flex justify-content-center">
                    {group.title}
                  </span>
                </div>
              )}
            >
              <TimelineHeaders>
                <SidebarHeader>
                  {({getRootProps}) => {
                    const style = {...getRootProps().style, height: '50px'};
                    return (
                      <div
                        className="bg-light justify-content-center
                         d-flex text-black align-items-center "
                        style={style}
                      >
                        指派人員
                      </div>
                    );
                  }}
                </SidebarHeader>
                <DateHeader
                  intervalRenderer={({getIntervalProps, intervalContext}) => {
                    const style = {
                      ...getIntervalProps().style,
                      height: '50px',
                    };
                    return (
                      <div
                        className="text-center bg-light d-flex
                         align-items-center justify-content-center "
                        style={style}
                      >
                        {intervalContext.intervalText}
                      </div>
                    );
                  }}
                />
              </TimelineHeaders>
            </Timeline>
            {dragItem && (
              <InfoLabel item={dragItem.item} time={dragItem.time} />
            )}
          </>
        ) : (
          <Calendar
            components={components}
            events={events}
            culture={culture}
            messages={messages}
            date={date}
            onNavigate={handleDateChange}
            localizer={localizer}
            views={views}
            view={currentView}
            onView={(view) => setCurrentView(view)}
            selectable
            onSelectSlot={handleSelectSlot}
            style={{height: '100vh', width: '100%'}}
          />
        )}
      </section>
    </>
  );
};

const Item = ({itemContext, item, getItemProps, getResizeProps}) => {
  let color = itemContext.selected ? '#FE7A7B' : '#3174ad';
  const status = item.eventData.flgStatus;
  color =
    status === enumList.assignmentFlgStatus.FINISH.value ? '#cccccc' : color;
  const style = {
    ...getItemProps().style,
    backgroundColor: color,
    background: color,
    borderColor: color,
  };
  return (
    <div {...getItemProps()} style={style}>
      <div
        className="rct-item-content"
        style={{maxHeight: `${itemContext.dimensions.height}`}}
      >
        {itemContext.title}
      </div>
    </div>
  );
};
const InfoLabel = ({item, time}) => {
  const date = moment(time, 'x');

  return (
    <div
      style={{
        position: 'fixed',
        left: '40%',
        bottom: 50,
        background: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        padding: 10,
        fontSize: 20,
        borderRadius: 5,
        zIndex: 85,
      }}
    >
      {`${item.title}-${date.format('LLL')}`}
    </div>
  );
};
export default DispatchCalendarPage;
